import { useState, useEffect, useCallback } from 'react';
import { useFilters } from '../context/FilterProvider';
import { getCachedNewTest, getCachedEditTests, getCachedProfile } from '../util/CacheUtil';

export function useTestEditing({ user, bloodTestFiles, effectiveSelectedFile, userFiles }) {
    const { selectFile, clearFilters } = useFilters();
    const [editTests, setEditTests] = useState(null);
    const [newTest, setNewTest] = useState(null);

    // Restore cached test editing state when user authenticates
    useEffect(() => {
        if (!user || user === undefined) return;
        const cachedNewTest = getCachedNewTest();
        if (cachedNewTest) setNewTest(cachedNewTest);
        const cachedEditTests = getCachedEditTests();
        setEditTests(cachedEditTests || {});
    }, [user]);

    // Sync newTest to sessionStorage
    useEffect(() => {
        if (newTest) {
            sessionStorage.setItem("newTest", JSON.stringify(newTest));
        } else {
            sessionStorage.removeItem("newTest");
        }
    }, [newTest]);

    // Sync editTests to sessionStorage (skip null = not yet initialized)
    useEffect(() => {
        if (editTests === null) return;
        sessionStorage.setItem("editTests", JSON.stringify(editTests));
    }, [editTests]);

    const addEditTest = useCallback(() => {
        if (typeof effectiveSelectedFile !== 'number' || !bloodTestFiles?.[effectiveSelectedFile]) return;
        setEditTests(prev => ({
            ...prev,
            [bloodTestFiles[effectiveSelectedFile].key]: bloodTestFiles[effectiveSelectedFile]
        }));
    }, [effectiveSelectedFile, bloodTestFiles]);

    const updateEditTest = useCallback((newValue) => {
        if (typeof effectiveSelectedFile !== 'number' || !bloodTestFiles?.[effectiveSelectedFile]) return;
        if (newValue == null) {
            setEditTests(prev => {
                const copy = { ...prev };
                delete copy[bloodTestFiles[effectiveSelectedFile].key];
                return copy;
            });
        } else {
            setEditTests(prev => ({
                ...prev,
                [bloodTestFiles[effectiveSelectedFile].key]: newValue
            }));
        }
    }, [effectiveSelectedFile, bloodTestFiles]);

    const createNewTest = useCallback(() => {
        const profileData = getCachedProfile();
        const temp = {
            data: {
                header: {
                    sex: profileData?.sex === 'male' ? 'M' : (profileData?.sex === 'female' ? 'F' : 'M'),
                    first_name: profileData?.firstName || '',
                    last_name: profileData?.lastName || '',
                    birthdate: profileData?.birthDate || ''
                },
                body: [{}]
            }
        };
        setNewTest(temp);
        selectFile("new");
        clearFilters();
    }, [selectFile, clearFilters]);

    return {
        editTests,
        setEditTests,
        newTest,
        setNewTest,
        addEditTest,
        updateEditTest,
        createNewTest,
    };
}
